/**
 * The main layout component of the DREM POC application frontend.
 * Renders the chat interface and handles user interactions with the chatbot.
 */
import { useRef, useState, useEffect, useCallback } from "react";

import styles from "./index.module.css";

import {
    chatApi,
    Approaches,
    AskResponse,
    ChatRequest,
    ChatTurn,
    LikeRequest,
    likeApi,
    InteractionType,
    ChatTimer,
    statApi,
    chatApiStream,
    setLanguageApi
} from "@/api";
import Messages from "@/components/Messages";
import { parseAnswerToText } from "@/components/Answer/AnswerParser";
import { AnalysisPanel, AnalysisPanelTabs } from "@/components/AnalysisPanel";
import TabChat from "@/components/TabChat";

import { useSelector, useDispatch } from "react-redux";

import { answerStreaming, hasTalkState, loadingStatus, toggleAvatar } from "@/components/Chat/chat";

import { RootState } from "@/store";

import { DIDConnect, DIDdestroy, DIDUploadFile, DIDTalkAudioURL } from "./streaming-client-api";
import { useTranslation } from "react-i18next";

import ChatHeader from "../ChatHeader";

import { useLocation } from "react-router-dom";

import { setSelectedValue } from "@/components/ChatHeader/chatSlice";
import i18n from "i18next";


let startTimer: number;
let conversationID: string = "";
let lastIterationID: string = "";

let lastAnswerData_points: string[] | null = null;
let lastAnswerThoughts: string[] | null = null;


const Chat = () => {
    const { t } = useTranslation();

    let timeTable: ChatTimer = { backend: 0, avatarTTS: 0, avatar: 0 };
    let errorMessage = t("settings.errorApi");

    const isLanguageSelected = useSelector((state: RootState) => state.chat.selectedValue);
    const avatarSound = useSelector((state: RootState) => state.values.isSoundOn);

    let languageValue = null;
    if (isLanguageSelected && typeof isLanguageSelected.value === "string") {
        languageValue = isLanguageSelected.value;
    }
    const isAvatarOn = useSelector((state: RootState) => state.values.isAvatarOn);
    const talkState = useSelector((state: RootState) => state.values.talkState);
    const isFullScreen = useSelector((state: RootState) => state.chat.isFullScreen);
    const dispatch = useDispatch();

    const [windowSize, setWindowSize] = useState([window.innerWidth, window.innerHeight]);
    const retrieveCount = 80;
    const useSuggestFollowupQuestions = true;
    const lastQuestionRef = useRef<string>("");

    const [avatarVideoTag, setAvatarVideoTag] = useState<HTMLMediaElement>();
    const [audioTag, setAudioTag] = useState<HTMLMediaElement>();

    const isAvatarOnRef = useRef<boolean>(isAvatarOn);
    isAvatarOnRef.current = isAvatarOn;

    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>();
    const [question, setQuestion] = useState<string>("");
    const [lastResponse, setLastResponse] = useState<AskResponse>();
    const [streamedAnswer, setStreamedAnswer] = useState<string>("");

    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: AskResponse][]>([]);

    let streamedAnswerGlobal = "";

    const handleResponse = (response: AskResponse) => {
        timeTable.backend = Date.now() - startTimer;
        conversationID = response.conversation_id;
        // TODO: clean this up
        // lastIterationID = response.iteration_id;
        startTimer = Date.now();
        setLastResponse(response);
        if(isAvatarOn){
            return isAvatarOnRef.current && renderAvatarAnimation(response);
        } else {
            return renderSoundResponse(response);
        }

    };

    const buildAnswer = useCallback(
        (q: string, a: string, thoughts: string | string[] | null, data_points: string[] | null): [user: string, response: AskResponse] => {
            const _lastResponse = {
                answer: a,
                conversation_id: conversationID,
                iteration_id: lastIterationID,
                thoughts: thoughts,
                data_points: data_points
            };
            setLastResponse(_lastResponse);
            return [q, _lastResponse];
        },
        [setLastResponse]
    );

    /**
     * Handles the incoming stream of data and updates the state accordingly.
     * @param chunk - The incoming chunk of data as a string.
     */

    const handleStream = (chunk: string, firstTime: boolean) => {
        const trimmedChunk = chunk.trim();
        const chunks = trimmedChunk.split("\n\n").filter((ch: string) => ch !== "");
        const formattedChunks = chunks.map((ch: string) => ch.replace(/\\n/g, "<br/>"));

        let dataFound = firstTime;

        const updateState = (content: any) => {
            if (content.system) {
                const { conversation_id, iteration_id } = content.system;
                if (conversation_id) conversationID = conversation_id;
                if (iteration_id) lastIterationID = iteration_id;
                dispatch(loadingStatus(true));
            }

            if (content.data) {
                if (!dataFound) {
                    timeTable.backend = Date.now() - startTimer;
                    sendStats(timeTable);
                }
                dataFound = true;
                dispatch(answerStreaming(true));
                dispatch(loadingStatus(false));
                streamedAnswerGlobal += content.data;
            }
            setStreamedAnswer(streamedAnswerGlobal);
        };

        formattedChunks.forEach((ch: string) => {
            try {
                const content = JSON.parse(ch);
                updateState(content);
            } catch (e) {
                console.error("Error parsing:", e);
            } finally {
                dispatch(answerStreaming(false));
            }
        });

        return dataFound;
    };

    /**
     * Resets the state of the component after a stream has ended.
     * @param err - The error message to be displayed.
     */
    const resetStream: (err?: string) => void = err => {
        dispatch(loadingStatus(false));
        streamedAnswerGlobal = "";
        if (err) {
            setError(new Error(String(err)));
        }
    };

    /**
     * Updates the last response in the answers array with the provided answer,
     * thoughts and data points.
     * @param newAnswer - The new answer to update the last response with.
     * @returns void
     */
    const updateLastResponse = useCallback(
        (newAnswer: string): void => {
            let prevAnswers = [...answers];
            if (prevAnswers?.length === 0) return;

            let lastAnswer = prevAnswers[prevAnswers.length - 1];
            if (lastAnswer[1].answer === newAnswer) return;

            let newThoughts = lastAnswerThoughts ? lastAnswerThoughts : lastAnswer[1].thoughts;
            let newData_points = lastAnswerData_points ? lastAnswerData_points : lastAnswer[1].data_points;
            prevAnswers[prevAnswers.length - 1] = buildAnswer(lastAnswer[0], newAnswer, newThoughts, newData_points);

            setAnswers(prevAnswers);

            // Reset global vars
            lastAnswerData_points = null;
            lastAnswerThoughts = null;
        },
        [answers, buildAnswer, setAnswers]
    );

    const makelikeAPIRequest = async (like: boolean, iteration_id: string) => {
        console.log("SendLike: ", lastIterationID, conversationID);
        const request: LikeRequest = {
            conversation_id: conversationID,
            iteration_id: iteration_id,
            like: like,
            reason: ""
        };
        await likeApi(request);
    };

    const sendStats = async (timeTable: ChatTimer) => {
        console.log("SendStats: ", lastIterationID, conversationID);
        await statApi(timeTable, lastIterationID, conversationID);
    };

    const prepareRequestData = (
        question: string,
        interactionType: InteractionType,
        answers: ChatTurn[],
        isAvatarOn: boolean,
        retrieveCount: number,
        useSuggestFollowupQuestions: boolean
    ): ChatRequest => {
        const chatRequest: ChatRequest = {
        user_message: question,
        // TODO: clean this up
        /*
        history: [...answers, { user: question, bot: undefined }],
        approach: Approaches.ReadRetrieveRead,
        interaction_type: interactionType,
        is_avatar: isAvatarOn,
        is_avatar_sound: avatarSound,
        layout_width: windowSize[0].toString(),
        layout: window.navigator.userAgent,
        overrides: {
            quantile: retrieveCount,
            suggestFollowupQuestions: useSuggestFollowupQuestions
        },
        */
        };

        if (conversationID) chatRequest.conversation_id = conversationID;

        return chatRequest;
    };

    const handleApiError = (e: any, isAvatarOn: boolean) => {
        console.error("makeApiRequest Error:", e);
        if (isAvatarOn) {
            const errorResponse = {
                answer: errorMessage,
                conversation_id: conversationID,
                iteration_id: lastIterationID,
                thoughts: null,
                data_points: null
            };
            renderAvatarAnimation(errorResponse);
        }
        setError(new Error(String(errorMessage)));
    };

    const makeApiRequest = async (question: string, interactionType: InteractionType) => {
        startTimer = Date.now();
        lastQuestionRef.current = question;
        if (error) setError(undefined);
        if (isAvatarOnRef.current) dispatch(hasTalkState("waitAnswer"));
        dispatch(loadingStatus(true));
        setActiveAnalysisPanelTab(undefined);

        const requestData = prepareRequestData(
            question,
            interactionType,
            answers.map(a => ({ user: a[0], bot: a[1].answer })),
            isAvatarOnRef.current,
            retrieveCount,
            useSuggestFollowupQuestions
        );
        try {
            // TODO: set this in config
            const isStreamDisabled = true;
            if (!isAvatarOn && !avatarSound && !isStreamDisabled){
                chatApiStream(requestData, handleStream, resetStream);
                renderQuestion(lastQuestionRef.current);
            } else {
                const response = await chatApi(requestData);
                if(isAvatarOn){
                    handleResponse(response);
                }else{
                    // only sound
                    renderResponse(response);
                    handleResponse(response);
                }


            }
        } catch (e) {
            handleApiError(e, isAvatarOnRef.current);
        } finally {
            if (!isAvatarOnRef.current) dispatch(loadingStatus(false));
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        conversationID = "";
    };

    const handleDataChannelMessage = useCallback(
        (pcDataChannel: { onmessage?: (message: MessageEvent<any>) => void; readyState: any }) => {
            if (!pcDataChannel || pcDataChannel.onmessage) return;

            dispatch(hasTalkState("wait"));
            pcDataChannel.onmessage = (message: MessageEvent) => {
                const state = pcDataChannel.readyState;
                if (state === "open" && message.data) {
                    const started = message.data === "stream/started";
                    if (started) {
                        setTimeout(() => {
                            dispatch(hasTalkState("talk"));
                        }, 1000);
                    } else {
                        dispatch(hasTalkState("wait"));
                    }
                }
            };
        },
        [dispatch]
    );

    const DIDConnectAndLink = useCallback(async () => {
        try {
            const pcDataChannel = await DIDConnect(avatarVideoTag, true);
            handleDataChannelMessage(pcDataChannel);
        } catch (e) {
            console.error("Error in DIDConnectAndLink: ", e);
            dispatch(hasTalkState("wait"));
            // await DIDConnect(avatarVideoTag, true);
        }
    }, [avatarVideoTag, dispatch, handleDataChannelMessage]);

    /**
     * Makes the avatar talk given a response object.
     * @param textAnswer The text to be spoken by the synthesizer.
     */
    const renderAvatarAnimation = async (response: AskResponse) => {
        function handleNoAudio() {
            // When the audio fails, we just show the text answer
            sendStats(timeTable);
            renderResponse(response);
            dispatch(toggleAvatar());
        }

        function handleNoAvatar(audioData: ArrayBuffer){
            const audioBlob = new Blob([audioData], { type: "audio/wav" });
            const audioFile = URL.createObjectURL(audioBlob);
            audioTag!.src = audioFile
            sendStats(timeTable);
            renderResponse(response);
        }


        const textAnswer = parseAnswerToText(response.chat_message);
        const result = await synthesizer.synthesizeText(textAnswer);
        if (!result.audioData) {
            handleNoAudio();
            return;
        }

        timeTable.avatarTTS = Date.now() - startTimer;
        startTimer = Date.now();

        return DIDUploadFile(result.audioData)
            .then(url => {
                DIDTalkAudioURL(url)
                    .then(() => {
                        timeTable.avatar = Date.now() - startTimer;
                        sendStats(timeTable);
                    })
                    .catch(() => {
                        // 2nd attempt
                        DIDdestroy();
                        DIDConnectAndLink()
                            .then(() => {
                                return DIDTalkAudioURL(url);
                            })
                            .catch(() => handleNoAvatar(result.audioData));
                    });
            })
            .catch(e => {
                // Catch DIDUploadFile errors
                console.error("DIDUploadFile error: ", e);
            });
    };

    const renderSoundResponse = async (response: AskResponse) => {
        function handleNoAudio() {
            // When the audio fails, we just show the text answer
            sendStats(timeTable);
            renderResponse(response);
            dispatch(toggleAvatar());
        }
        const textAnswer = parseAnswerToText(response.chat_message);
        const result = await synthesizer.synthesizeText(textAnswer);
        if (!result.audioData) {
            handleNoAudio();
            return;
        }
        timeTable.avatarTTS = Date.now() - startTimer;
        startTimer = Date.now();

        const audioBlob = new Blob([result.audioData], { type: "audio/wav" });
        const audioFile = URL.createObjectURL(audioBlob);
        audioTag!.src = audioFile
        sendStats(timeTable);
        return true
    };

    /**
     * Displays a question in the component's state.
     */
    const renderQuestion = (question: string): void => {
        setAnswers([...answers, buildAnswer(question, "", null, null)]);
    };

    /**
     * Displays a response in the component's state.
     */
    const renderResponse = useCallback(
        (response: AskResponse | undefined): void => {
            dispatch(loadingStatus(false));
            response && setAnswers(prevAnswers => [...prevAnswers, [lastQuestionRef.current, response]]);
        },
        [dispatch]
    );

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }
        setSelectedAnswer(index);
    };

    useEffect(() => {
        const displayResponseWhileTalking = (talkState: string): void => {
            if (talkState !== "talk") return;
            renderResponse(lastResponse);
        };

        displayResponseWhileTalking(talkState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [talkState, lastResponse]);

    useEffect(() => {
        if (streamedAnswer && streamedAnswer !== "") {
            updateLastResponse(streamedAnswer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streamedAnswer]);

    useEffect(() => {
        if (isAvatarOn && avatarVideoTag) {
            DIDConnectAndLink();
        }
    }, [DIDConnectAndLink, avatarVideoTag, isAvatarOn]);

    useEffect(() => {
        const handleWindowResize = (): void => {
            setWindowSize([window.innerWidth, window.innerHeight]);
        };

        window.addEventListener("resize", handleWindowResize);

        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, []);

    const [showConversationId, setShowConversationId] = useState(false);

    const location = useLocation();
    const isDevMode = location.pathname === '/dev_mode';

    useEffect(() => {
        if (isDevMode) {
            setShowConversationId(true);
        }
    }, [isDevMode]);

    useEffect(() => {
        if (!isLanguageSelected) {
            // Assume the language as "pt-PT" and dispatch it
            const defaultLanguage = "pt-PT";
            dispatch(setSelectedValue({ value: defaultLanguage })); // Set Redux state
            i18n.changeLanguage(defaultLanguage); // Update i18n
        }
    }, [isLanguageSelected, dispatch]);


return (
    <div>
        <div className={`${styles.wrapper} ${isFullScreen && styles.fullscreen}`}>
            <>  
                <ChatHeader onClose={clearChat} />
                {/* <ControlBar setAudioTag={setAudioTag} cleanChat={clearChat} /> */}

                <div id="chatContainer" className={styles.chatMainContainer}>
                    <div className={`${styles.chatRoot} ${isFullScreen && styles.fullscreen}`}>
                        {/* Messages Component */}
                        <Messages
                            answers={answers}
                            selectedAnswer={selectedAnswer}
                            activeAnalysisPanelTab={activeAnalysisPanelTab}
                            useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                            lastQuestionRef={lastQuestionRef}
                            error={error}
                            makeApiRequest={makeApiRequest}
                            makelikeAPIRequest={makelikeAPIRequest}
                        />

                        {/* Conditionally render conversation ID when showConversationId is true */}
                        {showConversationId && (() => {
                            const uniqueIds = new Set();
                            return answers.map(([user, response]) => {
                                if (uniqueIds.has(response.conversation_id)) return null;
                                uniqueIds.add(response.conversation_id);
                                return (
                                    <div key={response.iteration_id} className={styles.conversationContainer}>
                                        <div className={styles.conversationId}>
                                            Conversation ID: {response.conversation_id}
                                        </div>
                                    </div>
                                );
                            });
                        })()}

                        {/* TabChat Component */}
                        <TabChat
                            setIsRecording={setIsRecording}
                            question={question}
                            setQuestion={setQuestion}
                            makeApiRequest={makeApiRequest}
                            isRecording={isRecording}
                            onClick={clearChat}
                            disabled={answers.length === 0}
                            setAvatarVideoTag={setAvatarVideoTag}
                            sendQuestion={question => makeApiRequest(question, InteractionType.avatarAudioInput)}
                        />
                    </div>
                </div>
            </>

            {/* Analysis Panel (if any answers exist and a tab is active) */}
            {answers.length > 0 && activeAnalysisPanelTab && (
                <AnalysisPanel
                    className={styles.chatAnalysisPanel}
                    onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                    answer={answers[selectedAnswer][1]}
                    activeTab={activeAnalysisPanelTab}
                />
            )}
        </div>
    </div>
);
};

export default Chat;
